import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/library/select";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/library/card";
import FormField from "@/components/ui/others/FormField";
import SortableFieldGrid from "../SortableFieldGrid";
import { useLeadForm } from "@/features/leads/contexts/FormLeadContext";
import { useLeadViewFieldOrder } from "@/features/leads/hook/useLeadViewFieldOrder";
import useLeadSource from "@/features/sourceAndCampaign/api/useLeadSource";
import { useCampaigns } from "@/features/sourceAndCampaign/api/useCampaigns";
import { Controller } from "react-hook-form";

export default function LeadSourceEditMode() {
  const { control } = useLeadForm();
  const { sourceSelects } = useLeadSource();
  const { campaigns, isLoading: campaignsLoading } = useCampaigns({
    isActive: true,
  });
  // Tenant-wide field order (Super Admins drag the grips; the view mode
  // reads the same `source` / `campaign` keys).
  const sourceOrder = useLeadViewFieldOrder("source");

  const fields = sourceOrder.sortByOrder(
    [
      {
        key: "source",
        node: (
          <FormField label="Source">
            <Controller
              control={control}
              name="leadSourceId"
              render={({ field }) => (
                <Select onValueChange={field.onChange} value={field.value}>
                  <SelectTrigger className="bg-[#fcfcfc] border-[var(--color-black-2)] h-[40px] rounded-[12px] px-[12px] text-[14px] text-[#1e302a] tracking-[-0.56px] w-full">
                    <SelectValue placeholder="Select source" />
                  </SelectTrigger>
                  <SelectContent>
                    {sourceSelects?.map((source: any) => (
                      <SelectItem key={source.value} value={source.value}>
                        {source.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
          </FormField>
        ),
      },
      {
        key: "campaign",
        node: (
          <FormField label="Campaign">
            <Controller
              control={control}
              name="campaignId"
              render={({ field }) => (
                <Select
                  onValueChange={field.onChange}
                  value={field.value || ""}
                  disabled={campaignsLoading}
                >
                  <SelectTrigger className="bg-[#fcfcfc] border-[var( --color-black-2)] h-[40px] rounded-[12px] px-[12px] text-[14px] text-[var( --color-black-9)] tracking-[-0.56px] w-full">
                    <SelectValue
                      placeholder={
                        campaignsLoading
                          ? "Loading campaigns..."
                          : campaigns && campaigns.length > 0
                            ? "Select campaign"
                            : "No campaigns available"
                      }
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {(campaigns ?? []).map((c) => (
                      <SelectItem key={c.id} value={String(c.id)}>
                        <span className="flex items-center gap-2">
                          <span>{c.name}</span>
                          <span
                            className="text-[10px] uppercase tracking-wide text-[var(--color-text-gray)]"
                          >
                            {c.origin === "META" ? "Meta" : "Custom"}
                          </span>
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
          </FormField>
        ),
      },
    ],
    (f) => f.key,
  );

  return (
    <Card className="border-[var(--color-black-1)] rounded-[16px] gap-[16px] p-[16px] shadow-none">
      <CardHeader className="px-0 pb-0">
        <CardTitle className="text-[16px] font-medium text-black tracking-[-0.32px] leading-normal">
          Lead Source & Assignment
        </CardTitle>
      </CardHeader>
      <CardContent className="px-0 pt-0">
        <SortableFieldGrid
          columns={2}
          items={fields}
          canReorder={sourceOrder.canReorder}
          onReorder={sourceOrder.save}
        />
      </CardContent>
    </Card>
  );
}
